import { Wallet } from 'ethers';
import randomBytes from 'randombytes';
import Cookies from 'js-cookie';
import _, { isEqual } from 'lodash';

import {
  UUID_REG_EXP,
  REQUEST_PROD_URL,
  COOKIES_OPTIONS,
  SUPERUSER_LEVEL,
  AGGREGATE_LIMIT_TYPE,
  RATE_LIMIT_TYPE,
} from '../constants';
import { DEPLOYMENT_TIER } from './appConfig';

export const reflow = (node) => {
  // eslint-disable-next-line no-void
  void node.offsetHeight;
};

export const getNodeHeight = (node) => {
  return node.scrollHeight;
};

export const parseParamData = (match) => {
  let params = window.location.search.substring(1);
  params = params.split('&');
  const data = [];
  for (let i = 0; i < params.length; i += 1) {
    if (params[i].startsWith(match)) {
      const param = params[i].split('=');
      data.push({
        key: param[0],
        val: param.length > 1 ? param[1] : '',
      });
    }
  }
  return data;
};

export const getUrlParams = () =>
  [...new URLSearchParams(document.location.search).entries()].reduce(
    (q, [k, v]) =>
      Object.assign(q, {
        [k]: v.replace(' ', '+'),
      }),
    {}
  );

export const getInitials = (name) => {
  const regexChar = /\D\w+/;
  return name
    .trim()
    .split(' ')
    .filter((word) => word.length > 0)
    .filter((word) => regexChar.test(word))
    .map((word) => word.substring(0, 1).toUpperCase());
};

export const hasPermissions = (user, slug, rule) => {
  const currentRule =
    // eslint-disable-next-line no-shadow
    user && user.permissions && user.permissions.find((rule) => rule.target === slug);
  return !!(currentRule && currentRule.level.includes(rule));
};

export const hasSuperuserAccess = (user, levels) => {
  return (
    // comment out this line to mock admin.silamoney.com
    window.location.href.includes('admin.silamoney.com') &&
    user.is_superuser &&
    levels.includes(user.superuser_level)
  );
};

export const hasSalesAccess = (user) => {
  return (
    window.location.href.includes('admin.silamoney.com') &&
    user.superuser_level >= SUPERUSER_LEVEL.SALES
  );
};

export const isAdminView = () => {
  return window.location.href.includes('/admin/');
};

export const showAppCreationButton = (env) => {
  if (window.location.href.includes('admin.silamoney.com')) return true;
  if (DEPLOYMENT_TIER === 'stage' || DEPLOYMENT_TIER === 'local') {
    return true;
  }
  if (env === 'sandbox') return true;
  return false;
};

export const hasExpired = (date) => Date.parse(date) < new Date().getTime();

export const filterEmpty = (obj, [key, val]) => {
  if (typeof val === 'boolean' || val) {
    // eslint-disable-next-line no-use-before-define, no-param-reassign
    obj[key] = removeEmpty(val);
  }
  if (obj instanceof Array) {
    return obj.some((value) => Object.keys(value).length === 1 && value.action)
      ? []
      : obj.filter((value) => Object.keys(value).length !== 0);
  }
  return obj;
};

export const removeEmpty = (entry) => {
  if (entry instanceof Object) {
    const type = entry instanceof Array ? [] : {};
    const entries = Object.entries(entry);
    return entries.reduce(filterEmpty, type);
  }
  return entry;
};

export const getQueryString = (params) => {
  const esc = encodeURIComponent;
  return Object.keys(params)
    .map((k) => `${esc(k)}=${esc(params[k])}`)
    .join('&');
};

export const stripTrailingSlash = (str) =>
  str.substr(-1) === '/' ? str.substr(0, str.length - 1) : str;

export const generateWallet = (callback) => {
  const privateKey = randomBytes(32).toString('hex');
  const generated = new Wallet(privateKey);
  const wallet = {
    blockchain_address: generated.address,
    privateKey: privateKey,
  };
  if (callback) callback(wallet);
  return wallet;
};

export const simpleHandle = (handle) => handle.toLowerCase().replace(/.silamoney.eth/g, '');

export const formatHandle = (handle) => `${handle}.silamoney.eth`;

export const formatRole = (t, role) => t(`settings.members.labels.roles.${role}`);

export const formatRoleID = (role) =>
  // eslint-disable-next-line no-nested-ternary
  role === 'owner' ? 1 : role === 'manager' ? 2 : role === 'developer' ? 3 : 4;

export const formatMd5Name = (original, updated) =>
  original && original.replace('Team ', '').match(/^[a-f0-9]{32}$/gm) ? updated : original;

export const formatDateAndTime = (timestamp, showTime = true) => {
  const pad = (n) => (n < 10 ? `0${n}` : n);
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = pad(1 + date.getMonth());
  const day = pad(date.getDate());
  const time = date.toLocaleTimeString('en-US');
  return `${month}/${day}/${year}${showTime ? ` ${time}` : ''}`;
};

export const formatInternationalDateAndTime = (date, showTime, locale) => {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(showTime && { hour: '2-digit', minute: '2-digit' }),
  };
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
};

export const formatAddress = (data) => {
  const address = [];
  if (data.street_address_1) address.push(data.street_address_1);
  if (data.street_address_2) address.push(data.street_address_2);
  if (data.city) address.push(`${data.city},`);
  if (data.state) address.push(`${data.state},`);
  if (data.postal_code) address.push(data.postal_code);
  return address.join(' ');
};

export const formatNum = (num, digits) => {
  const units = ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
  let decimal;
  for (let i = units.length - 1; i >= 0; i -= 1) {
    decimal = 1000 ** (i + 1);
    if (num <= -decimal || num >= decimal) {
      return +(num / decimal).toFixed(digits) + units[i];
    }
  }
  return num;
};

export const formatNumberColumns = (num) =>
  num && num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// eslint-disable-next-line consistent-return
export const capitalize = (str) => {
  try {
    return str.toString().charAt(0).toUpperCase() + str.toString().slice(1);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error capitalize:', e.message);
  }
};

export const disAllowPassword = (passValue, emailValue, usernameValue) => {
  let validPassword = true;
  if (!passValue && !emailValue) return validPassword;

  const disallowPasswordFormats = [
    emailValue,
    `${emailValue}$`,
    `${emailValue}password`,
    `password${emailValue}`,
    `pass${emailValue}$$`,
  ];

  if (passValue && usernameValue && usernameValue !== null) {
    disallowPasswordFormats.push(usernameValue);
    disallowPasswordFormats.push(`${usernameValue}$`);
    disallowPasswordFormats.push(`${usernameValue}password`);
    disallowPasswordFormats.push(`password${usernameValue}`);
    disallowPasswordFormats.push(`pass${usernameValue}$$`);
  }

  if (disallowPasswordFormats.length > 0) {
    for (let i = 0; i < disallowPasswordFormats.length; i += 1) {
      if (disallowPasswordFormats[i] && disallowPasswordFormats[i].includes(passValue)) {
        validPassword = false;
        break;
      }
    }
  }

  return validPassword;
};

export const setUniqueCookie = (cName, handle) => {
  const currentYear = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(currentYear.getFullYear() + 1);
  Cookies.set(`${cName}_${handle}`, new Date().toString(), {
    ...COOKIES_OPTIONS,
    expires: nextYear,
  });
};

export const requestProdAccess = (t, user) => {
  window.open(REQUEST_PROD_URL, '_blank');
  setUniqueCookie('sila_console_prequalification', user.entity.handle);
};

export const handleResponseError = (response) => {
  let error = '';
  const iterateErrors = (errors, parent) => {
    if (typeof errors === 'object') {
      Object.entries(errors).forEach(([key, value], index) => {
        if (typeof errors[key] === 'object') {
          iterateErrors(errors[key], key);
        } else {
          error += `(${parent ? `${parent}.${key}` : key}) ${value.split('.').join('')}`;
        }
        // eslint-disable-next-line no-nested-ternary
        error += Object.keys(errors).length - 1 !== index ? ', ' : !parent ? '.' : '';
      });
    } else {
      error = errors;
    }
  };
  if (response.validation_details) {
    iterateErrors(response.validation_details);
  } else if (response.message) {
    error = response.message.replace(/\\/g, '');
  } else {
    error = JSON.stringify(response);
  }
  return error;
};

export const isValidJson = (json) => {
  try {
    JSON.parse(json);
  } catch (e) {
    return false;
  }
  return true;
};

// eslint-disable-next-line func-names
export const uniqueFieldValue = function (name, message, mapper = (a) => a) {
  // eslint-disable-next-line func-names
  return this.test('unique', message, function (list) {
    const set = [...new Set(list.map(mapper))];
    const isUnique = list.length === set.length;
    if (isUnique) {
      return true;
    }
    throw this.createError({
      path: `${this.path}[${list.findIndex((l, i) => mapper(l) !== set[i])}].${name}`,
      message,
    });
  });
};

export const formatFieldArray = (values, props, callback) => {
  Object.values(values).map((value) => {
    if (value instanceof Array) {
      // eslint-disable-next-line array-callback-return
      return value.map((val) => {
        // eslint-disable-next-line no-param-reassign
        if (val.tempId) delete val.tempId;
        // eslint-disable-next-line no-param-reassign
        if (!val.uuid && !val.id) val.action = 'add';
      });
    }
    return value;
  });
  if (callback) callback(values, props);
};

export const injectPdfjsCdn = (version) =>
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.mjs`;

export const truncateFileName = (name, max) => {
  const filename = name.substring(0, name.lastIndexOf('.'));
  const extension = name.split('.').pop();
  return name.length > max ? `${filename.substring(0, max)}.${extension}` : name;
};

export const isValidUUID = (str) => UUID_REG_EXP.test(str);

export const getUniqueArray = (arr = [], compareProps = []) => {
  const modifiedArray = [];
  if (compareProps.length === 0 && arr.length > 0) compareProps.push(...Object.keys(arr[0]));
  arr.forEach((item) => {
    if (modifiedArray.length === 0) {
      modifiedArray.push(item);
    } else if (
      !modifiedArray.some((item2) =>
        compareProps.every((eachProps) => item2[eachProps] === item[eachProps])
      )
    ) {
      modifiedArray.push(item);
    }
  });
  return modifiedArray;
};

export const applyFilter = ({ filters = [], sortBy = [] }) => {
  // eslint-disable-next-line no-shadow
  const formatFilter = ({ filters, sortBy }) => {
    const activeFilters = {
      filters: [],
      sortBy: [],
    };
    if (sortBy && sortBy.length) {
      sortBy.forEach((sort) => {
        const term = `&sort_keys=${sort.id}${sort.desc ? '_desc' : ''}`;
        if (activeFilters.sortBy.some((q) => q.includes(sort.id))) {
          activeFilters.sortBy.map((q) => (q.includes(sort.id) ? term : q));
        } else {
          activeFilters.sortBy.push(term);
        }
      });
    } else {
      activeFilters.sortBy = [];
    }
    if (filters && filters.length) {
      filters.forEach((filter) => {
        let term;
        if (filter.value instanceof Array) {
          filter.value.forEach((value) => {
            term = `&${value.term ? `${filter.id}SearchTerm` : filter.id}=${value.term || value}`;
            if (!activeFilters.filters.some((q) => q.includes(term)))
              activeFilters.filters.push(term);
          });
        } else {
          term = `&${filter.id}=${filter.value}`;
          if (!activeFilters.filters.some((q) => q.includes(term)))
            activeFilters.filters.push(term);
        }
      });
    } else {
      activeFilters.filters = [];
    }
    return [...activeFilters.sortBy, ...activeFilters.filters].join('');
  };
  return {
    query: formatFilter({
      filters: filters.query,
      sortBy: sortBy.query,
    }).replace(/SearchTerm/g, ''),
    url: formatFilter({
      filters: filters.url,
      sortBy: sortBy.url,
    }).substring(1),
  };
};

export const applyQuery = (query) => {
  const activeFilters = {
    filters: [],
    sortBy: [],
  };
  if (query.split('&').length) {
    query.split('&').forEach((param) => {
      const term = param.split('=');
      let termId;
      let termValue;
      if (term.length) {
        // eslint-disable-next-line prefer-destructuring
        termId = term[0];
        // eslint-disable-next-line prefer-destructuring
        termValue = term[1];
        try {
          termValue = decodeURI(termValue);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('ERROR DECODING URI');
        }
        if (termId.includes('sort_keys')) {
          activeFilters.sortBy.push({
            id: termValue.replace(/_desc/g, ''),
            desc: termValue.includes('desc'),
          });
        } else if (
          activeFilters.filters.some(
            (filter) =>
              (!termId.includes('SearchTerm') &&
                filter.id === termId &&
                filter.value.some((value) => value !== termValue)) ||
              (termId.includes('SearchTerm') &&
                filter.id === termId.replace(/SearchTerm/g, '') &&
                filter.value.some((value) => value.term && value.term !== termValue))
          )
        ) {
          activeFilters.filters.forEach((filter, index) => {
            const found = activeFilters.filters.findIndex(
              // eslint-disable-next-line no-shadow
              (filter) => filter.id === termId.replace(/SearchTerm/g, '')
            );
            if (found === index)
              activeFilters.filters[index] = {
                ...filter,
                value: [
                  ...filter.value,
                  filter.value.some((value) => value.term)
                    ? {
                        term: termValue,
                      }
                    : termValue,
                ],
              };
          });
        } else if (termId.includes('SearchTerm')) {
          activeFilters.filters.push({
            id: termId.replace(/SearchTerm/g, ''),
            value: [
              {
                term: termValue,
              },
            ],
          });
        } else if (termId && termValue) {
          activeFilters.filters.push({
            id: termId,
            value: [termValue],
          });
        }
      }
    });
  }
  return activeFilters;
};

export const convertToUSD = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value * 0.01)
    .replace(/\$/g, '');

export const parseDate = (date) => {
  return new Date(`${date}.000Z`);
};

export const convertDateToUTC = (date) => {
  const newDate = new Date(date);
  return new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60 * 1000).toISOString();
};

export const converUTCDatetoLocal = (date) => {
  const newDate = parseDate(date);
  return `${newDate.getFullYear()}-${`0${newDate.getMonth() + 1}`.slice(
    -2
  )}-${`0${newDate.getDate()}`.slice(-2)}`;
};

export const calcPercent = (num) => (num / 100) * 100;

export const truncate = (string, max) => {
  return string.length > max ? `${string.substring(0, max)}...` : string;
};

export const uploadDocument = async ({ url, file, data, onUpload, onError, onSuccess }) => {
  const xhr = new XMLHttpRequest();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('data', JSON.stringify(data));
  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      onUpload(Math.floor((event.loaded / event.total) * 100));
    }
  };
  xhr.onreadystatechange = () => {
    if (xhr.readyState !== 4) return;
    const json = JSON.parse(xhr.responseText);
    const { message } = json;
    if (xhr.status === 200) {
      onSuccess(message);
    } else {
      onError(xhr.status, handleResponseError(json));
    }
  };
  xhr.open('POST', `/api/v2${url}`, true);
  // xhr.setRequestHeader('Authorization', Bearer ${auth.token});
  xhr.send(formData);
};

export const configureChart = (options) => {
  const { xlabel, ylabel, title } = options;
  return {
    responsive: true,
    layout: {
      padding: {
        left: 20,
        right: 20,
      },
    },
    scales: {
      x: {
        display: true,
        reverse: true,
        title: {
          display: true,
          text: xlabel,
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: ylabel,
          font: {
            family: 'Inter, sans-serif',
          },
        },
      },
    },
    plugins: {
      tooltip: {
        intersect: false,
        enabled: true,
        titleFont: {
          family: 'Inter, sans-serif',
        },
        bodyFont: {
          family: 'Inter, sans-serif',
        },
        footerFont: {
          family: 'Inter, sans-serif',
        },
      },
      title: {
        display: true,
        color: '#00146B',
        font: {
          size: 16,
          family: 'Inter, sans-serif',
          weight: 'normal',
        },
        text: title,
      },
    },
  };
};

/**
 * Formats a limit object by converting duration from seconds to days
 *  and amount to a from cents to dollars, and all time values to UTC.
 *
 * @param {Object} limit - The limit object to be formatted.
 */
export function formatLimitResponse(limit) {
  if (limit.expires_at) {
    // eslint-disable-next-line no-param-reassign
    limit.expires_at = new Date(limit.expires_at).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (limit.begins_at) {
    // eslint-disable-next-line no-param-reassign
    limit.begins_at = new Date(limit.begins_at).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (limit.duration !== null) {
    // eslint-disable-next-line no-param-reassign
    limit.duration /= 24 * 60 * 60;
  }

  if (limit.amount !== null) {
    // eslint-disable-next-line no-param-reassign
    if (limit.limit_type === AGGREGATE_LIMIT_TYPE) limit.amount /= 100;
  }

  return limit;
}

/**
 * Formats the limits array within the response object by
 *  formatting each limit's duration from seconds to days
 *  and amount to a from cents to dollars.
 *
 * @param {Object} response - The response object containing the limits array.
 */
export function formatLimitsResponse(response) {
  if (response.limits && Array.isArray(response.limits)) {
    response.limits = response.limits.map((limit) => {
      return formatLimitResponse(limit);
    });
  }
}

/**
 * Formats a limit query object by converting duration from
 *  days to seconds and amount from dollars to cents and time values
 * to UTC.
 *
 * @param {Object} limit - The limit query object to be formatted.
 * @returns {Object} The formatted limit query object.
 */
export function formatLimitQuery(data, initialValues) {
  if (data.duration && data.duration !== null) {
    // eslint-disable-next-line no-param-reassign
    data.duration *= 24 * 60 * 60;
  }

  if (data.amount && data.amount !== null) {
    let limitType;
    if (data.limit_type && data.limit_type === AGGREGATE_LIMIT_TYPE) {
      limitType = AGGREGATE_LIMIT_TYPE;
    } else if (data.limit_type && data.limit_type === RATE_LIMIT_TYPE) {
      limitType = RATE_LIMIT_TYPE;
    } else {
      limitType = initialValues.limit_type;
    }
    // eslint-disable-next-line no-param-reassign
    if (limitType === AGGREGATE_LIMIT_TYPE) data.amount *= 100;
  }

  if (data.limit_type) {
    if (data.limit_type === AGGREGATE_LIMIT_TYPE && !data.amount) {
      // eslint-disable-next-line no-param-reassign
      data.amount = initialValues.amount * 100;
    }
    // eslint-disable-next-line no-param-reassign
    if (data.limit_type === RATE_LIMIT_TYPE && !data.amount) data.amount = initialValues.amount;
  }

  if (data.begins_at) {
    // eslint-disable-next-line no-param-reassign
    data.begins_at = new Date(data.begins_at).toISOString();
  }

  if (data.expires_at) {
    // eslint-disable-next-line no-param-reassign
    data.expires_at = new Date(data.expires_at).toISOString();
  }

  return data;
}

export const toTitleCase = (string) => {
  if (!string) return '';
  const allCaps = ['EIN', 'SSN', 'KYC', 'KYB', 'CKO', 'RTP', 'FEDNOW', 'ACHNOW'];
  return string
    .split('_')
    .map((word) => {
      if (allCaps.includes(word.toUpperCase())) {
        return ` ${word.toUpperCase()}`;
      }

      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(' ');
};

export const handleEnterKeyPress = (cb) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    cb();
    e.preventDefault();
  }
};

export const generateKYCFlowStatus = (flow) => {
  if (!flow) return {};
  if (flow.approved) return { canEdit: false, description: 'Approved' };
  if (flow.pending_decision) return { canEdit: false, description: 'Pending Approval' };
  if (!flow.approved && !flow.pending_decision && flow.approval_requested) {
    return { canEdit: true, description: 'Denied' };
  }
  return { canEdit: true, description: 'In Progress' };
};

export const unsortedArraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = arr1.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  const sortedArr2 = arr2.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  for (let i = 0; i < sortedArr1.length; i += 1) {
    if (sortedArr1[i].toLowerCase() !== sortedArr2[i].toLowerCase()) {
      return false;
    }
  }

  return true;
};

export const formatFreezeReasons = (initialFreezeReasons, currentFreezeReasonsSet) => {
  return Array.from(new Set([...initialFreezeReasons, ...currentFreezeReasonsSet])).reduce(
    (acc, reason) => {
      acc[reason] = currentFreezeReasonsSet.has(reason);
      return acc;
    },
    {}
  );
};

export const generateFilterDate = (date, range = 0, isStart = false) => {
  const selectedDate = new Date(date);
  selectedDate.setDate(selectedDate.getDate() + range);
  return `${selectedDate.toISOString().substring(0, 10)}T${isStart ? '00:00:00' : '23:59:59'}`;
};

const normalizeArray = (arr, key) => {
  return arr.slice().sort((a, b) => (a[key] > b[key] ? 1 : -1));
};

const normalizeObject = (obj) => {
  return {
    ...obj,
    sortBy: normalizeArray(obj.sortBy, 'id'),
    filters: normalizeArray(obj.filters, 'id'),
  };
};

export const deepCompareFilters = (obj1, obj2) => {
  const normalizedObj1 = normalizeObject(obj1);
  const normalizedObj2 = normalizeObject(obj2);
  return _.isEqual(normalizedObj1, normalizedObj2);
};

export const generateDefaultFilter = () => {
  const defaultCreatedFilter = {
    id: 'created',
    value: [generateFilterDate(new Date(), -30, true), generateFilterDate(new Date())],
  };
  return defaultCreatedFilter;
};

export const shallowCompareObjects = (object, base) => {
  const result = {};

  Object.keys(object).forEach((key) => {
    const newValue = object[key];
    const oldValue = base[key];
    if (!isEqual(newValue, oldValue)) result[key] = newValue;
  });

  return result;
};

export const truncateSilaId = (silaId) => {
  return `${silaId.slice(0, 7)}*`;
};
